import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  GraduationCap,
  Award,
  DollarSign,
  CreditCard,
  Plus,
  X,
  Save,
  UserPlus,
  CheckCircle,
  XCircle,
  AlertCircle,
  IndianRupee,
} from "lucide-react";
import {
  createDoctorDetails,
  checkDoctorEmailExists,
} from "../../../../services/doctorApi";

const AddDoctorForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: "",
    dateOfBirth: "",
    gender: "",
    contact: {
      mobile: "",
      email: "",
      address: "",
    },
    specialization: [],
    experience: "",
    education: [{ degree: "", institution: "", year: "" }],
    certifications: [],
    consultationFee: "",
    bankDetails: {
      accountNumber: "",
      ifscCode: "",
      bankName: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailExists, setEmailExists] = useState(null);

  // Available specializations
  const specializations = [
    "General Medicine",
    "Pediatrics",
    "Cardiology",
    "Dermatology",
    "Orthopedics",
    "Neurology",
    "Gynecology",
    "Psychiatry",
    "Radiology",
    "Anesthesiology",
    "Emergency Medicine",
    "Internal Medicine",
    "Surgery",
    "Oncology",
    "Ophthalmology",
    "ENT",
    "Urology",
    "Endocrinology",
    "Gastroenterology",
    "Pulmonology",
  ];

  const genderOptions = ["Male", "Female"];

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check if email exists
  const checkEmailExists = async (email) => {
    if (!email || !validateEmail(email)) {
      setEmailExists(null);
      return;
    }

    try {
      setEmailChecking(true);
      const response = await checkDoctorEmailExists(email);
      setEmailExists(response.exists);
    } catch (error) {
      setEmailExists(null);
    } finally {
      setEmailChecking(false);
    }
  };

  // Real-time email checking
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkEmailExists(formData.contact.email);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.contact.email]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSpecializationChange = (specialization) => {
    setFormData((prev) => ({
      ...prev,
      specialization: prev.specialization.includes(specialization)
        ? prev.specialization.filter((s) => s !== specialization)
        : [...prev.specialization, specialization],
    }));
  };

  // Education handlers
  const addEducation = () => {
    setFormData((prev) => ({
      ...prev,
      education: [...prev.education, { degree: "", institution: "", year: "" }],
    }));
  };

  const removeEducation = (index) => {
    setFormData((prev) => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index),
    }));
  };

  const updateEducation = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      education: prev.education.map((edu, i) =>
        i === index ? { ...edu, [field]: value } : edu
      ),
    }));
  };

  // Certification handlers
  const addCertification = () => {
    setFormData((prev) => ({
      ...prev,
      certifications: [
        ...prev.certifications,
        {
          name: "",
          issuedBy: "",
          issuedDate: "",
          expiryDate: "",
          certificateUrl: "",
        },
      ],
    }));
  };

  const removeCertification = (index) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index),
    }));
  };

  const updateCertification = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) =>
        i === index ? { ...cert, [field]: value } : cert
      ),
    }));
  };

  // Form validation
  const validateForm = () => {
    const errors = [];

    if (!formData.name.trim()) errors.push("Name is required");
    if (!formData.contact.email.trim()) errors.push("Email is required");
    if (!validateEmail(formData.contact.email))
      errors.push("Valid email is required");
    if (emailExists) errors.push("Email already exists for another doctor");
    if (!formData.contact.mobile.trim())
      errors.push("Mobile number is required");
    if (!formData.gender) errors.push("Gender is required");
    if (formData.specialization.length === 0)
      errors.push("At least one specialization is required");

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(", "));
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      // Clean up empty education entries
      const cleanedData = {
        ...formData,
        education: formData.education.filter(
          (edu) => edu.degree.trim() || edu.institution.trim() || edu.year
        ),
        certifications: formData.certifications.filter(
          (cert) => cert.name.trim() || cert.issuedBy.trim()
        ),
      };

      const response = await createDoctorDetails(cleanedData);

      if (response.success) {
        setSuccess("Doctor profile created successfully!");
        setTimeout(() => {
          if (onSuccess) onSuccess(response.data);
        }, 1500);
      }
    } catch (error) {
      setError(error.message || "Failed to create doctor profile");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      dateOfBirth: "",
      gender: "",
      contact: { mobile: "", email: "", address: "" },
      specialization: [],
      experience: "",
      education: [{ degree: "", institution: "", year: "" }],
      certifications: [],
      consultationFee: "",
      bankDetails: { accountNumber: "", ifscCode: "", bankName: "" },
    });
    setError("");
    setSuccess("");
    setEmailExists(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Mobile-Optimized Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                  Add New Doctor
                </h1>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Create a comprehensive doctor profile
                </p>
              </div>
            </div>
            {onCancel && (
              <button
                onClick={onCancel}
                className="self-end sm:self-center p-2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Status Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <span className="text-green-700 text-sm sm:text-base">
              {success}
            </span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <span className="text-red-700 text-sm sm:text-base">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 flex items-center">
                <User className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Personal Information
              </h3>
            </div>

            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2 lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="Enter full name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Gender *
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      required
                    >
                      <option value="">Select Gender</option>
                      {genderOptions.map((gender) => (
                        <option key={gender} value={gender}>
                          {gender}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Experience (Years)
                    </label>
                    <input
                      type="number"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 flex items-center">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Contact Information
              </h3>
            </div>

            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      name="contact.mobile"
                      value={formData.contact.mobile}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="+91 9876543210"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email Address *
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        name="contact.email"
                        value={formData.contact.email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 sm:py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="doctor@example.com"
                        required
                      />
                      {formData.contact.email &&
                        validateEmail(formData.contact.email) && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            {emailChecking ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            ) : emailExists === false ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : emailExists === true ? (
                              <XCircle className="w-4 h-4 text-red-500" />
                            ) : null}
                          </div>
                        )}
                    </div>
                    {formData.contact.email &&
                      validateEmail(formData.contact.email) &&
                      emailExists === true && (
                        <p className="mt-1.5 text-xs text-red-600">
                          Email already exists
                        </p>
                      )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Address
                  </label>
                  <textarea
                    name="contact.address"
                    value={formData.contact.address}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base resize-none"
                    placeholder="Enter complete address"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Specialization */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 flex items-center">
                <Award className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Specialization *
              </h3>
            </div>

            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {specializations.map((specialization) => (
                  <label
                    key={specialization}
                    className="flex items-start space-x-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={formData.specialization.includes(specialization)}
                      onChange={() =>
                        handleSpecializationChange(specialization)
                      }
                      className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      {specialization}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Education */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-base sm:text-lg font-medium text-gray-900 flex items-center">
                  <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Education
                </h3>
                <button
                  type="button"
                  onClick={addEducation}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center justify-center space-x-2 text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Education</span>
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                {formData.education.map((edu, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  >
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="sm:col-span-2 lg:col-span-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Degree
                          </label>
                          <input
                            type="text"
                            value={edu.degree}
                            onChange={(e) =>
                              updateEducation(index, "degree", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="e.g., MBBS, MD"
                          />
                        </div>

                        <div className="sm:col-span-2 lg:col-span-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Institution
                          </label>
                          <input
                            type="text"
                            value={edu.institution}
                            onChange={(e) =>
                              updateEducation(
                                index,
                                "institution",
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="Institution name"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Year
                          </label>
                          <div className="flex space-x-2">
                            <input
                              type="number"
                              value={edu.year}
                              onChange={(e) =>
                                updateEducation(index, "year", e.target.value)
                              }
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              placeholder="2020"
                              min="1950"
                              max="2030"
                            />
                            {formData.education.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeEducation(index)}
                                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                aria-label="Remove education"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Certifications */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-base sm:text-lg font-medium text-gray-900 flex items-center">
                  <Award className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Certifications
                </h3>
                <button
                  type="button"
                  onClick={addCertification}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center justify-center space-x-2 text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Certification</span>
                </button>
              </div>
            </div>

            {formData.certifications.length > 0 && (
              <div className="p-4 sm:p-6">
                <div className="space-y-4">
                  {formData.certifications.map((cert, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                    >
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Certification Name
                            </label>
                            <input
                              type="text"
                              value={cert.name}
                              onChange={(e) =>
                                updateCertification(
                                  index,
                                  "name",
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              placeholder="Certification name"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Issued By
                            </label>
                            <div className="flex space-x-2">
                              <input
                                type="text"
                                value={cert.issuedBy}
                                onChange={(e) =>
                                  updateCertification(
                                    index,
                                    "issuedBy",
                                    e.target.value
                                  )
                                }
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                placeholder="Issuing authority"
                              />
                              <button
                                type="button"
                                onClick={() => removeCertification(index)}
                                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                aria-label="Remove certification"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Issued Date
                            </label>
                            <input
                              type="date"
                              value={
                                cert.issuedDate
                                  ? new Date(cert.issuedDate)
                                      .toISOString()
                                      .split("T")[0]
                                  : ""
                              }
                              onChange={(e) =>
                                updateCertification(
                                  index,
                                  "issuedDate",
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Expiry Date
                            </label>
                            <input
                              type="date"
                              value={
                                cert.expiryDate
                                  ? new Date(cert.expiryDate)
                                      .toISOString()
                                      .split("T")[0]
                                  : ""
                              }
                              onChange={(e) =>
                                updateCertification(
                                  index,
                                  "expiryDate",
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Professional Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 flex items-center">
                <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Professional Details
              </h3>
            </div>

            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Consultation Fee (₹)
                  </label>
                  <input
                    type="number"
                    name="consultationFee"
                    value={formData.consultationFee}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    placeholder="500"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 sm:px-6 py-3 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 flex items-center">
                <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Bank Details (Optional)
              </h3>
            </div>

            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Account Number
                    </label>
                    <input
                      type="text"
                      name="bankDetails.accountNumber"
                      value={formData.bankDetails.accountNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="1234567890"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      IFSC Code
                    </label>
                    <input
                      type="text"
                      name="bankDetails.ifscCode"
                      value={formData.bankDetails.ifscCode}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="ABCD0123456"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    name="bankDetails.bankName"
                    value={formData.bankDetails.bankName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    placeholder="State Bank of India"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile-Optimized Action Buttons */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={resetForm}
                className="w-full sm:w-auto px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Reset Form
              </button>

              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="w-full sm:w-auto px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
              )}

              <button
                type="submit"
                disabled={loading || emailExists}
                className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg flex items-center justify-center space-x-2 transition-colors disabled:cursor-not-allowed text-sm font-medium"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Create Doctor Profile</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDoctorForm;
